import { useState, type FormEvent } from 'react';
import { useAddDnc, useDiallerDnc, useRemoveDnc } from '../../lib/api/hooks-dialler.js';
import { useSession } from '../../lib/auth/session.js';
import { formatNumber } from '../../lib/format.js';
import { Empty, ErrorBanner, SectionHead, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * Numbers this account never calls (docs/36 D1b). Adding one suppresses it on every list at once;
 * a lead arriving later with that number is kept and marked, never dialled.
 */
export function DncPage({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const [q, setQ] = useState('');
  const dnc = useDiallerDnc(tenantId, q);
  const add = useAddDnc(tenantId);
  const remove = useRemoveDnc(tenantId);
  const [numbers, setNumbers] = useState('');
  const [reason, setReason] = useState('');
  const [said, setSaid] = useState<string | null>(null);

  return (
    <>
      <SectionHead title="Do not call"
        sub="Numbers this account never dials, whichever list they are on or arrive on later." />

      {can('dialler:write') && (
        <form className="arc-card arc-card-body" style={{ marginBottom: 14 }} onSubmit={(e: FormEvent) => {
          e.preventDefault();
          const phones = numbers.split(/[\n,;]+/).map((n) => n.trim()).filter(Boolean);
          add.mutate({ phones, reason: reason.trim() || null }, {
            onSuccess: (r) => {
              setNumbers(''); setReason('');
              setSaid(`${formatNumber(r.added)} added${r.already ? `, ${formatNumber(r.already)} already on it` : ''}`
                + `${r.not_numbers ? `, ${formatNumber(r.not_numbers)} not phone numbers` : ''}`
                + `${r.suppressed_on_lists ? `. ${formatNumber(r.suppressed_on_lists)} waiting lead${r.suppressed_on_lists === 1 ? '' : 's'} will not be called` : ''}.`);
            },
          });
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 2fr) minmax(200px, 1fr)', gap: 12 }}>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="dnc-n">Numbers</label>
              <textarea id="dnc-n" className="arc-input arc-mono" rows={3} value={numbers}
                onChange={(e) => setNumbers(e.target.value)} placeholder={'082 123 4567\n+27 11 555 0100'} />
              <div className="arc-hint">One a line, or pasted from a spreadsheet column.</div>
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="dnc-r">Why (optional)</label>
              <input id="dnc-r" className="arc-input" value={reason} maxLength={200}
                onChange={(e) => setReason(e.target.value)} placeholder="Asked not to be called" />
            </div>
          </div>
          <div className="arc-row" style={{ gap: 10, marginTop: 10 }}>
            <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" disabled={add.isPending || !numbers.trim()}>Add</button>
            {said && <span className="arc-muted" style={{ fontSize: 12.5 }}>{said}</span>}
          </div>
          {add.error && <ErrorBanner error={add.error} />}
        </form>
      )}

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <input className="arc-input" placeholder="Part of a number…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a number" />
          </div>
          <div className="arc-spacer" />
          {dnc.data && <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>{formatNumber(dnc.data.total)} numbers</span>}
        </div>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Number</th><th>Why</th><th>Added</th><th /></tr></thead>
            {dnc.data === undefined ? <TableSkeleton cols={4} /> : (
              <tbody>
                {dnc.data.data.map((d) => (
                  <tr key={d.phone}>
                    <td className="arc-mono">+{d.phone}</td>
                    <td>{d.reason ?? <span className="arc-subtle">—</span>}</td>
                    <td className="arc-num">{new Date(d.created_at).toLocaleDateString('en-ZA')}</td>
                    <td className="arc-td-right">
                      {can('dialler:write') && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={remove.isPending}
                          onClick={() => { if (confirm(`Take +${d.phone} off do-not-call? Leads already marked stay marked.`)) remove.mutate(d.phone); }}>
                          <Icon name="trash" size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {dnc.data && dnc.data.total === 0 && <Empty icon="phone" title={q ? 'Not on the list' : 'Nobody on it yet'} />}
        {remove.error && <ErrorBanner error={remove.error} />}
      </div>
    </>
  );
}
